export function addZeroPrefix(length: number, value: any) {
  return '0'.repeat(length) + value;
}

export function padLeftNumeric(value: string | number, length: number): string {
  return value.toString().padStart(length, '0');
}

const digitsOnly = (value: string | number) => value.toString().replace(/\D/g, '');

export type BarcodeSegment = {
  start: number;
  length: number;
};

export type BarcodePattern = {
  game: BarcodeSegment;
  pack: BarcodeSegment;
  ticket: BarcodeSegment;
  lengthOfTicketNumber: number;
  lengthOfBarcode: number;
};

export function parseBarcodePattern(pattern: string): BarcodePattern {
  const parts = pattern.split('|').map((segment) => segment.trim());
  if (parts.length < 3) {
    throw new Error(`Invalid pattern '${pattern}': expected at least 3 segments`);
  }

  const [gamePart, packPart, ticketPart] = parts.slice(0, 3).map((segment) => {
    const [startStr, lengthStr] = segment.split(',').map((v) => v.trim());
    const start = Number(startStr);
    const length = Number(lengthStr);
    if (Number.isNaN(start) || Number.isNaN(length) || length <= 0 || start < 0) {
      throw new Error(`Invalid pattern segment '${segment}'`);
    }
    return { start, length };
  });

  const lengthOfTicketNumber = Math.max(
    gamePart.start + gamePart.length,
    packPart.start + packPart.length,
    ticketPart.start + ticketPart.length
  );
  const lengthOfBarcode = parts[3] ? Number(parts[3]) : lengthOfTicketNumber;
  if (Number.isNaN(lengthOfBarcode) || lengthOfBarcode <= 0) {
    throw new Error(`Invalid pattern '${pattern}': invalid barcode length segment`);
  }

  return {
    game: gamePart,
    pack: packPart,
    ticket: ticketPart,
    lengthOfTicketNumber,
    lengthOfBarcode,
  };
}

const resolveBarcodePattern = (params: {
  format?: string;
}): BarcodePattern => {
  if (!params.format) {
    throw new Error('Barcode format string is required');
  }
  const parts = params.format.split('|').map((segment) => segment.trim());
  if (parts.length < 3) {
    throw new Error(`Invalid barcode format '${params.format}': expected at least 3 segments`);
  }
  return parseBarcodePattern(params.format);
};

export function buildBarcodeRegex(format: string): RegExp {
  const parsed = resolveBarcodePattern({ format });
  const usedLength = parsed.game.length + parsed.pack.length + parsed.ticket.length;
  const tailLength = parsed.lengthOfTicketNumber - usedLength;

  if (tailLength < 0) {
    throw new Error(`Invalid pattern '${format}': total length ${parsed.lengthOfTicketNumber} is too small`);
  }

  const tail = tailLength > 0 ? `(?<rest>\\d{${tailLength}})` : '';
  const source = `^(?<game>\\d{${parsed.game.length}})(?<pack>\\d{${parsed.pack.length}})(?<ticket>\\d{${parsed.ticket.length}})${tail}$`;
  return new RegExp(source);
}

export function buildTicketBarcodeValue(params: {
  format?: string;
  gameNumber: string;
  packId: string;
  ticketNumber: number;
}): string {
  const pattern = resolveBarcodePattern(params);
  const totalLength = Math.max(pattern.lengthOfBarcode, pattern.lengthOfTicketNumber);
  const game = padLeftNumeric(digitsOnly(params.gameNumber), pattern.game.length).slice(-pattern.game.length);
  const pack = padLeftNumeric(digitsOnly(params.packId), pattern.pack.length).slice(-pattern.pack.length);
  const ticket = padLeftNumeric(digitsOnly(params.ticketNumber), pattern.ticket.length).slice(-pattern.ticket.length);

  const result = Array(totalLength).fill('0');
  result.splice(pattern.game.start, pattern.game.length, ...game.split(''));
  result.splice(pattern.pack.start, pattern.pack.length, ...pack.split(''));
  result.splice(pattern.ticket.start, pattern.ticket.length, ...ticket.split(''));

  const barcode = result.join('');
  const finalBarcode = barcode.padEnd(totalLength, '0');
  return finalBarcode;
}

export function buildTicketNumberValue(params: {
  format?: string;
  gameNumber: string;
  packId: string;
  ticketNumber: number;
}): string {
  const pattern = resolveBarcodePattern(params);
  const totalLength = pattern.lengthOfTicketNumber;
  const game = padLeftNumeric(digitsOnly(params.gameNumber), pattern.game.length).slice(-pattern.game.length);
  const pack = padLeftNumeric(digitsOnly(params.packId), pattern.pack.length).slice(-pattern.pack.length);
  const ticket = padLeftNumeric(digitsOnly(params.ticketNumber), pattern.ticket.length).slice(-pattern.ticket.length);

  const result = Array(totalLength).fill('0');
  result.splice(pattern.game.start, pattern.game.length, ...game.split(''));
  result.splice(pattern.pack.start, pattern.pack.length, ...pack.split(''));
  result.splice(pattern.ticket.start, pattern.ticket.length, ...ticket.split(''));

  return result.join('');
}

export function buildTicketDisplayNumber(params: {
  format?: string;
  gameNumber: string;
  packId: string;
  randomDigit: string;
  ticketNumber: number;
}): string {
  const pattern = resolveBarcodePattern(params);
  const game = padLeftNumeric(digitsOnly(params.gameNumber), pattern.game.length).slice(-pattern.game.length);
  const pack = padLeftNumeric(digitsOnly(params.packId), pattern.pack.length).slice(-pattern.pack.length);
  const ticket = padLeftNumeric(digitsOnly(params.ticketNumber), pattern.ticket.length).slice(-pattern.ticket.length);

  const display = `${game}-${pack}-${ticket}`;
  return display;
}

export function parseStartingTicketNumber(
  startingTicketNumber: number | string,
  options?: {
    format?: string;
  }
): {
  gameNumber: string;
  packId: string;
  randomDigit: string;
  initialTicketsSold: number;
} {
  const pattern = resolveBarcodePattern({
    format: options?.format,
  });
  const ticketStr = startingTicketNumber
    .toString()
    .padStart(pattern.lengthOfTicketNumber, '0');
  const parsedGameNumber = ticketStr.substring(pattern.game.start, pattern.game.start + pattern.game.length);
  const parsedPackId = ticketStr.substring(pattern.pack.start, pattern.pack.start + pattern.pack.length);
  const parsedRandomDigit = '0';
  const parsedInitialTicketsSold = parseInt(
    ticketStr.substring(pattern.ticket.start, pattern.ticket.start + pattern.ticket.length),
    10
  ) || 0;
  return {
    gameNumber: parsedGameNumber,
    packId: parsedPackId,
    randomDigit: parsedRandomDigit,
    initialTicketsSold: parsedInitialTicketsSold,
  };
}
